import { SectionCard } from '@/components/SectionCard'
import { UserAvatar } from '@/components/UserAvatar'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import { useAuth } from '@/features/auth/hooks/useAuth'
import { useConfirm } from '@/hooks/useConfirm'
import { useUI } from '@/hooks/useUI'
import { isApiError } from '@/utils/error'
import { showError, showSuccess } from '@/utils/toast'
import { Edit2, Eye, Loader2, Plus, Search, Trash2 } from 'lucide-react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useDeleteUser, useGetUsers } from '../hooks/useUser'
import { User } from '../types/user'
import { UserDetailDialog } from './UserDetailDialog'
import { UserDialog } from './UserDialog'

export function UsersView() {
  const { t } = useTranslation()
  const confirm = useConfirm()
  const { isAdmin } = useAuth()
  const [search, setSearch] = useState('')
  const [roleFilter, setRoleFilter] = useState<string>('all')
  const { userRoles, getEnumLabel } = useUI()
  const { data, isLoading } = useGetUsers(roleFilter === 'all' ? undefined : roleFilter)
  const deleteUser = useDeleteUser()

  const [selectedUserId, setSelectedUserId] = useState<string | null>(null)
  const [mode, setMode] = useState<'create' | 'edit'>('create')
  const [userOpen, setUserOpen] = useState(false)
  const [userDetailOpen, setUserDetailOpen] = useState(false)

  const displayUsers =
    data?.filter(
      user =>
        !search ||
        user.username?.toLowerCase().includes(search.toLowerCase()) ||
        user.name.toLowerCase().includes(search.toLowerCase()),
    ) ?? []

  async function handleDeleteUser(id: string) {
    if (!isAdmin) return

    const confirmed = await confirm({
      variant: 'destructive',
      title: t('common.deleteConfirmTitle'),
      description: t('users.deleteConfirmDescription'),
    })

    if (confirmed) {
      try {
        await deleteUser.mutate(id)
        showSuccess(t('users.deleteSuccess'))
      } catch (error) {
        if (isApiError(error)) showError(error.message)
      }
    }
  }

  return (
    <>
      <SectionCard
        title={t('users.manage')}
        right={
          <>
            <Select value={roleFilter} onValueChange={setRoleFilter}>
              <SelectTrigger className="h-8 w-[140px] bg-secondary/50 text-xs">
                <SelectValue placeholder={t('common.role')} />
              </SelectTrigger>
              <SelectContent className="bg-card text-foreground">
                <SelectItem value="all">{t('users.allRoles')}</SelectItem>
                {userRoles?.map(role => (
                  <SelectItem key={role.value} value={role.value}>
                    {role.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <div className="relative">
              <Search className="absolute left-2.5 top-1/2 size-3.5 -translate-y-1/2 text-muted-foreground" />
              <Input
                data-testid="users-search-input"
                placeholder={t('users.searchPlaceholder')}
                value={search}
                onChange={e => setSearch(e.target.value)}
                className="h-8 w-[200px] bg-secondary/50 pl-8 text-xs"
              />
            </div>
            {isAdmin && (
              <Button
                data-testid="users-create-button"
                size="sm"
                className="h-8 text-xs"
                onClick={() => {
                  setSelectedUserId(null)
                  setMode('create')
                  setUserOpen(true)
                }}
              >
                <Plus className="mr-1 size-3" />
                {t('users.addUser')}
              </Button>
            )}
          </>
        }
      >
        <div data-testid="users-table" className="rounded-lg border border-border/30">
          <Table className="table-auto w-full">
            <TableHeader>
              <TableRow className="border-border/30 hover:bg-transparent">
                <TableHead className="sr-only whitespace-nowrap text-xs">{t('common.avatar')}</TableHead>
                <TableHead className="truncate text-xs text-muted-foreground">{t('common.username')}</TableHead>
                <TableHead className="truncate text-xs text-muted-foreground">{t('common.name')}</TableHead>
                <TableHead className="truncate text-xs text-muted-foreground">{t('common.role')}</TableHead>
                <TableHead className="truncate text-xs text-muted-foreground">
                  {t('common.primaryDepartment')}
                </TableHead>
                <TableHead className="w-px sr-only whitespace-nowrap text-xs">{t('common.actions')}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {isLoading ? (
                <TableRow>
                  <TableCell colSpan={6} className="py-8 text-center text-sm text-muted-foreground">
                    {t('common.loading')}
                  </TableCell>
                </TableRow>
              ) : displayUsers.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} className="py-8 text-center text-sm text-muted-foreground">
                    {t('users.noUsers')}
                  </TableCell>
                </TableRow>
              ) : (
                displayUsers.map((user: User) => (
                  <TableRow key={user.id} data-user-id={user.id} className="border-border/20 hover:bg-secondary/40">
                    <TableCell className="w-px whitespace-nowrap">
                      <UserAvatar
                        name={user.name}
                        username={user.username}
                        avatarId={user.avatarId}
                        className="size-7 rounded-md border border-border/40"
                        fallbackClassName="rounded-md text-[10px] font-medium text-muted-foreground"
                      />
                    </TableCell>
                    <TableCell className="truncate text-xs font-mono text-foreground">{user.username}</TableCell>
                    <TableCell className="truncate text-xs text-muted-foreground">{user.name}</TableCell>
                    <TableCell className="truncate text-xs text-muted-foreground">
                      {getEnumLabel(userRoles, user.role)}
                    </TableCell>
                    <TableCell className="truncate text-xs text-muted-foreground">
                      {user.primaryDepartmentName || user.primaryDepartmentId || '-'}
                    </TableCell>
                    <TableCell className="w-px whitespace-nowrap">
                      <div className="flex items-center justify-end gap-1">
                        <Button
                          data-testid="user-view-button"
                          variant="ghost"
                          size="sm"
                          className="h-6 px-2 text-[10px] text-primary"
                          onClick={() => {
                            setSelectedUserId(user.id)
                            setUserDetailOpen(true)
                          }}
                        >
                          <Eye className="mr-1 size-3" />
                          {t('common.view')}
                        </Button>
                        {isAdmin && (
                          <>
                            <Button
                              data-testid="user-edit-button"
                              variant="ghost"
                              size="sm"
                              className="h-6 px-2 text-[10px] text-muted-foreground"
                              onClick={() => {
                                setSelectedUserId(user.id)
                                setMode('edit')
                                setUserOpen(true)
                              }}
                            >
                              <Edit2 className="mr-1 size-3" />
                              {t('common.edit')}
                            </Button>
                            <Button
                              data-testid={`user-delete-button`}
                              variant="ghost"
                              size="sm"
                              className="h-6 px-2 text-[10px] text-destructive hover:text-destructive hover:bg-destructive/10"
                              onClick={() => handleDeleteUser(user.id)}
                              disabled={deleteUser.isPending}
                            >
                              {deleteUser.isPending ? (
                                <Loader2 className="size-3 animate-spin" />
                              ) : (
                                <>
                                  <Trash2 className="mr-1 size-3" />
                                  {t('common.delete')}
                                </>
                              )}
                            </Button>
                          </>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </div>
      </SectionCard>

      <UserDialog open={userOpen} onOpenChange={setUserOpen} userId={selectedUserId} mode={mode} />
      <UserDetailDialog open={userDetailOpen} onOpenChange={setUserDetailOpen} userId={selectedUserId} />
    </>
  )
}
